import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getOrders,
  deleteAdmin,
  updateOrder,
  uploadReceipts,
  setPayableOrder,
  downloadReceipt,
} from "../../utils/backend";
import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const PurchaseHistory = () => {
  const { user_id } = useParams();

  const [orders, setorders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [transactionId, setTransactionID] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [fullName, setFullName] = useState("");
  const [id_no, setIdNo] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [insurancePlan, setInsurancePlan] = useState("");
  const [insurerName, setInsurerName] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [sumInsured, setSumInsured] = useState("");
  const [basicPremium, setBasicPremium] = useState("");
  const [addOnPremium, setAddOnPremium] = useState("");
  const [totalPremium, setTotalPremium] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");

  const hasPermission = (permissionGroup, permissionName) => {
    let permissions;
    try {
      permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      permissions = [];
    }

    return permissions.some(
      (perm) =>
        perm.permission_group === permissionGroup &&
        perm.permission_name === permissionName
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    transactionId,
    transactionDate,
    fullName,
    id_no,
    email,
    phoneNumber,
    insurerName,
    insurancePlan,
    vehicleType,
    vehicleNumber,
    sumInsured,
    basicPremium,
    addOnPremium,
    totalPremium,
    paymentStatusFilter,
    startDate,
    endDate,
    policyNumber,
  ]);

  const fetchData = async (filters = { perPage: perPage, order_type: "" }) => {
    // setIsLoading(true);
    try {
      const response = await getOrders(filters);
      setorders(setUppercase(response.data.data));
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setorders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveStateToLocalStorage = (currentPage) => {
    const existingState =
      JSON.parse(localStorage.getItem("purchaseState")) || {};
    const state = {
      user_id,
      fullName,
      email,
      insurancePlan,
      insurerName,
      vehicleType,
      vehicleNumber,
      sumInsured,
      basicPremium,
      addOnPremium,
      totalPremium,
      paymentStatusFilter,
      startDate,
      endDate,
      currentPage,
    };
    localStorage.setItem(
      "purchaseState",
      JSON.stringify({ ...existingState, ...state })
    );
  };

  const handleSearch = async () => {
    const filters = {
      user_id,
      transaction_id: transactionId,
      transaction_start_date: transactionDate,
      full_name: fullName,
      id_no,
      email,
      phone_number: phoneNumber,
      insurance_plan: insurancePlan,
      insurer_name: insurerName,
      vehicle_type: vehicleType,
      vehicle_number: vehicleNumber,
      sum_insured: sumInsured,
      basic_premium: basicPremium,
      add_on_premium: addOnPremium,
      total_premium: totalPremium,
      payment_status: paymentStatusFilter,
      policy_effective_date: startDate,
      policy_expiry_date: endDate,
      policy_no: policyNumber,
      page: 1,
      perPage: perPage,
    };
    saveStateToLocalStorage(1); // Save state before fetching data
    await fetchData(filters);
  };

  const handleReset = () => {
    setTransactionID("");
    setTransactionDate("");
    setFullName("");
    setIdNo("");
    setEmail("");
    setPhoneNumber("");
    setInsurancePlan("");
    setInsurerName("");
    setVehicleType("");
    setVehicleNumber("");
    setSumInsured("");
    setBasicPremium("");
    setAddOnPremium("");
    setTotalPremium("");
    setStartDate("");
    setEndDate("");
    setPolicyNumber("");
    setPaymentStatusFilter("");
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState =
      JSON.parse(localStorage.getItem("purchaseState")) || {};
    localStorage.setItem(
      "purchaseState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData({ user_id });
  };

  const handlePageChange = async (page) => {
    localStorage.setItem(
      "purchaseState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("purchaseState")),
        currentPage: page,
      })
    );

    const filters = {
      user_id,
      transaction_id: transactionId,
      transaction_start_date: transactionDate,
      full_name: fullName,
      id_no,
      email,
      phone_number: phoneNumber,
      insurance_plan: insurancePlan,
      insurer_name: insurerName,
      vehicle_type: vehicleType,
      vehicle_number: vehicleNumber,
      sum_insured: sumInsured,
      basic_premium: basicPremium,
      add_on_premium: addOnPremium,
      total_premium: totalPremium,
      payment_status: paymentStatusFilter,
      policy_effective_date: startDate,
      policy_expiry_date: endDate,
      policy_no: policyNumber,
      page: page,
      perPage: perPage,
    };
    await fetchData(filters);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "purchaseState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("purchaseState")),
        perPage: newPerPage,
        currentPage: 1,
      })
    );
    const filters = {
      user_id,
      transaction_id: transactionId,
      transaction_start_date: transactionDate,
      full_name: fullName,
      id_no,
      email,
      phone_number: phoneNumber,
      insurance_plan: insurancePlan,
      insurer_name: insurerName,
      vehicle_type: vehicleType,
      vehicle_number: vehicleNumber,
      sum_insured: sumInsured,
      basic_premium: basicPremium,
      add_on_premium: addOnPremium,
      total_premium: totalPremium,
      payment_status: paymentStatusFilter,
      policy_effective_date: startDate,
      policy_expiry_date: endDate,
      policy_no: policyNumber,
      page: 1,
      perPage: newPerPage,
    };
    await fetchData(filters);
  };

  const handlePaymentStatusChange = async (orderId, newStatus) => {
    const updatedStatus =
      newStatus.charAt(0).toUpperCase() + newStatus.slice(1);

    const originalOrder = orders.find((order) => order.id === orderId);
    const originalStatus = originalOrder ? originalOrder.payment_status : null;

    if (originalStatus === updatedStatus) {
      return;
    }

    setorders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId
          ? { ...order, payment_status: updatedStatus }
          : order
      )
    );

    try {
      const response = await updateOrder({
        id: orderId,
        payment_status: newStatus,
      });
      if (response.success) {
        toast.success("Payment status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update payment status");
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Error updating payment status");
      setorders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? { ...order, payment_status: originalStatus }
            : order
        )
      );
    }
  };

  const handleDownloadReceipt = async (orderId) => {
    try {
      const response = await downloadReceipt({ order_id: orderId });
      if (response.success) {
        if (response.data.receipt) {
          const link = document.createElement("a");
          link.href = response.data.receipt;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("Receipt downloaded successfully");
        } else {
          toast.error("Receipt not found");
        }
      } else {
        throw new Error(response.message || "Failed to download receipt");
      }
    } catch (error) {
      console.error("Error downloading receipt:", error);
      toast.error("Error downloading receipt");
    }
  };

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  return (
    <>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <>
              <CRow>
                <CCol className="justify-content-end d-flex my-1">
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <div style={{ overflowX: "auto" }}>
                <CTable borderless striped>
                  <CTableHead>
                    <CTableRow>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={transactionId}
                          onChange={(e) => setTransactionID(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="date"
                          id="start_date"
                          placeholder="Start Date"
                          value={transactionDate}
                          onChange={(e) => setTransactionDate(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={id_no}
                          onChange={(e) => setIdNo(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search Phone"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={insurerName}
                          onChange={(e) => setInsurerName(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={insurancePlan}
                          onChange={(e) => setInsurancePlan(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={vehicleType}
                          onChange={(e) => setVehicleType(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={vehicleNumber}
                          onChange={(e) => setVehicleNumber(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={sumInsured}
                          onChange={(e) => setSumInsured(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={basicPremium}
                          onChange={(e) => setBasicPremium(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={addOnPremium}
                          onChange={(e) => setAddOnPremium(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={totalPremium}
                          onChange={(e) => setTotalPremium(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormSelect
                          id="order_type"
                          value={paymentStatusFilter}
                          onChange={(e) =>
                            setPaymentStatusFilter(e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          <option value="Pending">Pending</option>
                          <option value="Completed">Completed</option>
                        </CFormSelect>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="date"
                          placeholder="Search"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="date"
                          placeholder="Search"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={policyNumber}
                          onChange={(e) => setPolicyNumber(e.target.value)}
                        />
                      </CTableDataCell>
                    </CTableRow>
                    <CTableRow>
                      <CTableHeaderCell>No</CTableHeaderCell>
                      {/* <CTableHeaderCell>Actions</CTableHeaderCell> */}
                      <CTableHeaderCell>Transaction ID</CTableHeaderCell>
                      <CTableHeaderCell>Transaction Date</CTableHeaderCell>
                      <CTableHeaderCell>Customer Name</CTableHeaderCell>
                      <CTableHeaderCell>Identification Number</CTableHeaderCell>
                      <CTableHeaderCell>Email Address</CTableHeaderCell>
                      <CTableHeaderCell>Phone Number</CTableHeaderCell>
                      <CTableHeaderCell>Insurance Company</CTableHeaderCell>
                      <CTableHeaderCell>Plan Name</CTableHeaderCell>
                      <CTableHeaderCell>Vehicle Type</CTableHeaderCell>
                      <CTableHeaderCell>Vehicle Number</CTableHeaderCell>
                      <CTableHeaderCell>Sum Insured</CTableHeaderCell>
                      <CTableHeaderCell>Basic Premium</CTableHeaderCell>
                      <CTableHeaderCell>Add-On Premium</CTableHeaderCell>
                      <CTableHeaderCell>Total Premium</CTableHeaderCell>
                      <CTableHeaderCell>Payment Status</CTableHeaderCell>
                      <CTableHeaderCell>Policy Effective Date</CTableHeaderCell>
                      <CTableHeaderCell>
                        Policy Expiry / Renewal Date
                      </CTableHeaderCell>
                      <CTableHeaderCell>Policy Number</CTableHeaderCell>
                      <CTableHeaderCell>Admin Notes</CTableHeaderCell>
                      
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {orders.map((order, index) => (
                      <CTableRow key={order.id}>
                        <CTableDataCell>
                          {index + 1 + perPage * (currentPage - 1)}
                        </CTableDataCell>
                        {/* <CTableDataCell>
                          <div className="d-flex align-items-center gap-2">
                            {hasPermission("Order Management", "View") && (
                              <CButton
                                className="btn btn-sm btn-info text-white"
                                onClick={() =>
                                  order.order_type === "inquiry"
                                    ? navigate(
                                        `/orders/inquiry/details/${order.id}`
                                      )
                                    : order.order_type === "quote"
                                    ? navigate(
                                        `/orders/api/details/${order.id}`
                                      )
                                    : order.order_type === "product"
                                    ? navigate(
                                        `/orders/product/details/${order.id}`
                                      )
                                    : null
                                }
                              >
                                <i className="bi bi-eye-fill"></i>
                              </CButton>
                            )}
                            {hasPermission("Policy Management", "Create") &&
                              order.order_type === "inquiry" && (
                                <>
                                  <div className="d-flex align-items-center pe-3">
                                    <CButton
                                      className="btn btn-sm btn-success text-white"
                                      onClick={() =>
                                        navigate(
                                          `/orders/inquiry/create-policy/${order.id}`
                                        )
                                      }
                                      disabled={
                                        order.order_status ===
                                          "Pending Payment" ||
                                        order.order_status === "In Progress"
                                      }
                                    >
                                      <i className="bi bi-file-earmark-plus"></i>
                                    </CButton>
                                    {(order.order_status ===
                                      "Pending Payment" ||
                                      order.order_status === "In Progress") && (
                                      <CTooltip
                                        content="Order status must be 'Paid' or 'Completed' before policy can be created"
                                        placement="right"
                                      >
                                        <span
                                          className="ms-2"
                                          style={{ marginLeft: "5px" }}
                                        >
                                          <i className="bi bi-info-circle"></i>
                                        </span>
                                      </CTooltip>
                                    )}
                                  </div>
                                </>
                              )}

                            {(order.order_status === "Paid" ||
                              order.order_status === "Completed") && (
                              <CButton
                                className="btn btn-sm btn-danger text-white"
                                onClick={() => handleDownloadReceipt(order.id)}
                              >
                                <i className="bi bi-download"></i>
                              </CButton>
                            )}
                          </div>
                        </CTableDataCell> */}
                        <CTableDataCell>
                          {(order.ipay_transactions &&
                            order.ipay_transactions.transaction_id) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.ipay_transactions &&
                          order.ipay_transactions.transaction_date
                            ? formatDateTime(
                                order.ipay_transactions.transaction_date
                              )
                            : "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.full_name ||
                            (order.order_details &&
                              order.order_details.full_name) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            order.motor_insurance.id_no) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.user_details && order.user_details.email) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.phone_number || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.insurer_name || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.insurance_plan || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            order.motor_insurance.veh_type) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            order.motor_insurance.reg_no) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            "RM" + order.motor_insurance.sum_insured) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            "RM" + order.motor_insurance.basic_premium) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            "RM" +
                              order.motor_insurance.extraCoverages
                                .reduce(
                                  (sum, item) =>
                                    sum + parseFloat(item.amount || 0),
                                  0
                                )
                                .toFixed(2)) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {(order.motor_insurance &&
                            "RM" + order.motor_insurance.total_payable) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          <CDropdown>
                            <CDropdownToggle
                              color="secondary"
                              variant="outline"
                              disabled={
                                !hasPermission("Order Management", "Edit")
                              }
                              style={{
                                backgroundColor:
                                  order.payment_status === "Completed"
                                    ? "#66BB6A"
                                    : "#A9A9A9",
                                color: "#fff",
                                border: "none",
                                width: "160px",
                                fontSize: "14px",
                                borderRadius: "20px",
                                fontWeight: "500",
                              }}
                            >
                              {order.payment_status
                                ? order.payment_status.toUpperCase()
                                : "N/A"}
                            </CDropdownToggle>
                            <CDropdownMenu>
                              <CDropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange(order.id, "Pending")
                                }
                              >
                                Pending
                              </CDropdownItem>
                              <CDropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange(
                                    order.id,
                                    "Completed"
                                  )
                                }
                              >
                                Completed
                              </CDropdownItem>
                            </CDropdownMenu>
                          </CDropdown>
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.policy_details?.policy_start_date
                            ? new Date(
                                order.policy_details?.policy_start_date
                              ).toLocaleString()
                            : "N/A"}
                        </CTableDataCell>{" "}
                        <CTableDataCell style={{ minWidth: "110px" }}>
                          {order.policy_details?.policy_end_date
                            ? new Date(
                                order.policy_details?.policy_end_date
                              ).toLocaleString()
                            : "N/A"}
                        </CTableDataCell>{" "}
                        <CTableDataCell>
                          {(order.policy_details &&
                            order.policy_details.cover_note_no) ||
                            "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.order_notes || "N/A"}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </div>
            </>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => {
                      const newPage = currentPage - 1;
                      handlePageChange(newPage);
                    }}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 ||
                      pageNumber === totalPages - 1 ||
                      pageNumber < 5 ||
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2)
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => {
                            const newPage = pageNumber + 1;
                            handlePageChange(newPage);
                          }}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) ||
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4)
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      handlePageChange(newPage);
                    }}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {orders.length} of {totalItems} orders
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default PurchaseHistory;
